import { Button, Grid, Stack, useTheme, CircularProgress, ButtonGroup } from "@mui/material";
import { Input, Col } from "antd";
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import PropTypes from 'prop-types';
import * as XLSX from "xlsx";
import TextInput from "@/common/components/text-input/text-input";
import indianNumberFormatComponent from "@/common/helpers/indianNumberFormatComponent";
import Dropdown from "@/common/components/dropdown/dropdown";
import SwitchInput from "@/common/components/switch-input/switch-input";
import AnimateButton from "@/from-theme/components/@extended/AnimateButton";
import { FormattedMessage } from "react-intl";
import useGetCityOptions from "@/common/hooks/useGetCityMaster";
import { apiStatuses } from "@/store/api/constants";
import { apiBaseUrl } from "@/store/api/constants/baseURL";
import Spinner from "@/common/components/spinner/spinner";
import ErrorInPage from "@/common/components/error-in-page/error-in-page";
import useGetPlantMasterByDivision from "@/common/hooks/useGetPlantByDivision";
import useCreateEditSchemeStatusMaster from "./hooks/useCreateEditSchemeStatusMaster";
import CustomTable from '@/common/components/CustomTableNew/CustomTable';
import { commonColumns } from "./constants/constants";
import useGetDropdownListOptions from "@/common/hooks/useGetDropdownListMaster";
import SchemeStatusService from "@/service/Scheme/SchemeStatusService";
import { ToastContainer, toast } from 'react-toastify';
import Swal from 'sweetalert2';

function CreateEditSchemeStatusMaster({ isEditMode }) {

  const { getCityRawData } = useGetCityOptions();
  const { averageRuleOptions, getDropdownListLoading } = useGetDropdownListOptions();

  const mappedAverageRuleOptions = averageRuleOptions.map((opt) =>
    ({ label: opt.label, id: opt.id }));
  const [show, setShow] = useState(false);
  const { getPlantsByDivisionOptions: plantsData,
    getPlantsByDivision: getPlant,
    getPlantsByDivisionLoading: plantsLoading
  } = useGetPlantMasterByDivision("plant");

  // 2. Add Filter State
  const [filterType, setFilterType] = useState('ALL'); // 'ALL', 'ELIGIBLE', 'NOT_ELIGIBLE'
  const [searchName, setSearchName] = useState('');
  const [searchRate, setSearchRate] = useState('');

  useEffect(() => {
    setShow(true);
  }, []);

  const {
    values, formik, getEditSchemesListMasterStatus,
    getEditSchemesListMasterErrMsg, updateSchemesListMasterLoading, rejectSchemesListMasterLoading,
    goBack, rejectProcess, competitorId
  } = useCreateEditSchemeStatusMaster({ isEditMode, getPlant, getCityRawData });

  const theme = useTheme();

  const filter3dStyle = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#f9f9f9',
      borderRadius: '12px',
      boxShadow: 'inset 2px 2px 5px #e0e0e0, inset -2px -2px 5px #ffffff, 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      border: '1px solid rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 'inset 1px 1px 3px #e0e0e0, inset -1px -1px 3px #ffffff, 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      },
      '&.Mui-focused': {
        transform: 'translateY(-2px)',
        backgroundColor: '#ffffff',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        borderColor: theme.palette.primary.main,
      }
    },
    '& .MuiInputBase-input': {
      fontWeight: 500,
      color: '#333',
    },
    '& .MuiInputLabel-root': {
      color: '#100979ff',
      fontWeight: 700,
      textShadow: '0.5px 0.5px 0px rgba(0,0,0,0.1)'
    }
  };

  const [avgRule, setAvgRule] = useState(false)
  const [isSalesDataLoading, setIsSalesDataLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});

  const {
    noOfCustomerCount,
    // schemeTotal,
    schemeName, productId,
    brandId, categoryId, materialId, schemeMonth,
    schemeYear, schemeId, totalSales,
    //divisionName, 
    childData, groupId,
    targetWorkingFile,
    //  productName // Ensure this is available from values
  } = values;

  const isMaida = useMemo(() => {
    return schemeName && schemeName.toLowerCase().includes('maida');
  }, [schemeName]);

  const baseData = useMemo(() => {
    if (!childData || childData.length === 0) return [];
    // Filter out rows where status = 2 as requested (Rejected/Removed)
    return childData.filter(row => Number(row.status) !== 2);
  }, [childData]);

  const getRowSchemeAmount = useCallback((row) => {
    if (!row) return 0;
    let amt = 0;
    if (row.new_scheme_amount !== undefined && row.new_scheme_amount !== null && row.new_scheme_amount !== "") {
      if (typeof row.new_scheme_amount === "object") {
        amt = Object.values(row.new_scheme_amount).reduce((s, v) => s + (parseFloat(v) || 0), 0);
      } else {
        amt = parseFloat(row.new_scheme_amount) || 0;
      }
    } else if (row.categoryEligibleAmounts !== undefined && row.categoryEligibleAmounts !== null && row.categoryEligibleAmounts !== "") {
      if (typeof row.categoryEligibleAmounts === "object") {
        amt = Object.values(row.categoryEligibleAmounts).reduce((s, v) => s + (parseFloat(v) || 0), 0);
      } else {
        amt = parseFloat(row.categoryEligibleAmounts) || 0;
      }
    } else if (row.schemeNewEligibleAmount !== undefined && row.schemeNewEligibleAmount !== null && row.schemeNewEligibleAmount !== "") {
      amt = parseFloat(row.schemeNewEligibleAmount) || 0;
    } else if (row.totalAmount !== undefined && row.totalAmount !== null && row.totalAmount !== "") {
      amt = parseFloat(row.totalAmount) || 0;
    } else if (row.costPerTon || row.new_scheme_rs_per_ton) {
      const salesVolume = typeof row.monthSales === 'object' ?
        Object.values(row.monthSales).reduce((s, v) => s + (parseFloat(v) || 0), 0) : parseFloat(row.monthSales || 0);
      const rate = row.new_scheme_rs_per_ton || row.costPerTon || 0;
      amt = Math.round(salesVolume * rate);
    }
    return amt;
  }, []);

  const calculatedTotalSchemeAmount = useMemo(() => {
    if (values?.totalAmount !== undefined && values?.totalAmount !== null && values?.totalAmount !== "") {
      return Number(values.totalAmount) || 0;
    }
    if (values?.totalSchemeAmount !== undefined && values?.totalSchemeAmount !== null && values?.totalSchemeAmount !== "") {
      return Number(values.totalSchemeAmount) || 0;
    }
    if (values?.schemeTotal !== undefined && values?.schemeTotal !== null && values?.schemeTotal !== "") {
      return Number(values.schemeTotal) || 0;
    }

    if (!baseData || !Array.isArray(baseData)) return 0;

    return baseData.reduce((sum, row) => sum + getRowSchemeAmount(row), 0);
  }, [values, baseData, getRowSchemeAmount]);

  const selectedTotalSchemeAmount = useMemo(() => {
    if (!childData || !Array.isArray(childData)) return 0;

    return childData.reduce((sum, row) => {
      if (Number(row.status) === 2 || Number(row.selected) !== 1) return sum;
      return sum + getRowSchemeAmount(row);
    }, 0);
  }, [childData, getRowSchemeAmount]);

  const filteredChildData = useMemo(() => {
    let data = baseData;

    // 1. Categorical Filter
    if (filterType === 'Eligible') {
      data = data.filter(row => {
        if (isMaida) {
          if (Number(row.eligible) === 1) return true;
          // For Maida, check category-wise eligibility manually if eligible flag isn't set
          let targets = row.targetMts || {};
          if (typeof targets === 'string' && targets.startsWith('{')) {
            try { targets = JSON.parse(targets); } catch (e) { targets = {}; }
          }
          if (!targets || typeof targets !== 'object') targets = {};
          const sales = row.monthSales || {};
          return Object.keys(targets).some(cat => {
            const t = Number(targets[cat] || 0);
            const s = Number(sales[cat] || 0);
            return t > 0 && s >= t;
          });
        }
        return Number(row.eligible) === 1;
      });
    } else if (filterType === 'Not Eligible') {
      data = data.filter(row => {
        if (isMaida) {
          let targets = row.targetMts || {};
          if (typeof targets === 'string' && targets.startsWith('{')) {
            try { targets = JSON.parse(targets); } catch (e) { targets = {}; }
          }
          if (!targets || typeof targets !== 'object') targets = {};
          const sales = row.monthSales || {};
          // Check if any target is defined
          const hasDefinedTarget = Object.values(targets).some(t => Number(t) > 0);
          if (!hasDefinedTarget) return false;

          // Check if ALL defined targets are missed
          const metAnyTarget = Object.keys(targets).some(cat => {
            const t = Number(targets[cat] || 0);
            const s = Number(sales[cat] || 0);
            return t > 0 && s >= t;
          });
          return !metAnyTarget;
        }
        const sales = Number(row.totalSalesVolume || 0);
        const target = Number(row.targetMts || row.minMts || row.maxMts || 0);
        return target > 0 && sales < target;
      });
    } else if (filterType === 'Unplanned') {
      data = data.filter(row => row.id === null);
    } else if (filterType === 'Below 5 Tons') {
      data = data.filter(row => {
        const sales = Number(row.totalSalesVolume || 0);
        return sales < 5;
      });
    } else if (filterType === 'Below 10 Tons') {
      data = data.filter(row => {
        const sales = Number(row.totalSalesVolume || 0);
        return sales < 10;
      });
    } else if (filterType === 'Zero Amount') {
      data = data.filter(row => {
        const amt = getRowSchemeAmount(row);
        return amt === 0;
      });
    }

    // 2. Name / Code Search (Allows multiple customer name/code search using CSV/space/newline format)
    if (searchName) {
      const searchTerms = searchName
        .split(/[\s,]+/)
        .map(term => term.trim().toLowerCase())
        .filter(term => term.length > 0);

      if (searchTerms.length > 0) {
        data = data.filter(row => {
          const nameLower = row.customerName ? String(row.customerName).toLowerCase() : '';
          const codeLower = row.customerCode ? String(row.customerCode).toLowerCase() : '';
          return searchTerms.some(term =>
            nameLower.includes(term) || codeLower.includes(term)
          );
        });
      }
    }

    // // 3. Rate Search (Rs/Kg or Per Ton)
    // if (searchRate) {
    //   data = data.filter(row => {
    //     const fieldsToSearch = [
    //       row.new_scheme_rs_per_kg,
    //       row.schemeRsPerKg,
    //       row.new_scheme_rs_per_ton,
    //       row.costPerTon
    //     ];
    //     return fieldsToSearch.some(val => {
    //       if (val === null || val === undefined) return false;
    //       if (typeof val === 'object') {
    //         return Object.values(val).some(v => String(v).includes(searchRate));
    //       }
    //       return String(val).includes(searchRate);
    //     });
    //   });
    // }

    return data;
  }, [baseData, filterType, searchName,
    //searchRate, 
    isMaida, getRowSchemeAmount]);



  const handleExport = () => {
    if (!values.childData || values.childData.length === 0) {
      alert("No data to export");
      return;
    }
    const dataToExport = values.childData.map(row => ({
      "Customer Code": row.customerCode,
      "Customer Name": row.customerName,
      "Sales MTS": row.salesMts,
      "Target MTS": row.targetMts,
      "Status": row.status,
      ...row
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Scheme Details");
    const fileName = schemeId ? `Scheme_${schemeId}.xlsx` : "Scheme_Details.xlsx";
    XLSX.writeFile(workbook, fileName);
  };

  // useEffect(() => {
  //   if (!values.childData?.length) return

  //   const manualRowIndexes = Object.keys(rowSelection).map(Number)
  //   const updatedChildData = values.childData.map((row, rowIndex) => {
  //     const isEligible =
  //       (row.targetMts && row.salesMts >= row.targetMts) ||
  //       (!row.targetMts &&
  //         row.salesMts >= Number(row.minMts) &&
  //         row.salesMts <= Number(row.maxMts))

  //     const isSelected =
  //       manualRowIndexes.includes(rowIndex) || isEligible

  //     return {
  //       ...row,
  //       eligible: isEligible ? 1 : 0,
  //       selected: isSelected ? 1 : 0
  //     }
  //   })
  //   formik.setFieldValue("childData", updatedChildData)

  //   if (avgRule) {
  //     formik.setFieldValue("avgRuleOverAll", 1)
  //     formik.setFieldValue("avgRuleOverAllGroupId", groupId ? groupId.id : "")
  //   } else {
  //     formik.setFieldValue("avgRuleOverAll", 0)
  //     formik.setFieldValue("avgRuleOverAllGroupId", "")
  //   }
  // }, [rowSelection, avgRule, groupId])

  useEffect(() => {
    if (avgRule) {
      formik.setFieldValue("avgRuleOverAll", 1);
      formik.setFieldValue("avgRuleOverAllGroupId", groupId ? groupId.id : "");
    } else {
      formik.setFieldValue("avgRuleOverAll", 0);
      formik.setFieldValue("avgRuleOverAllGroupId", "");
    }
  }, [avgRule, groupId]);

  const handleActionChange = (rowId, value) => {
    const newChildData = values.childData.map((row) => {
      if (row.id === rowId) { return { ...row, action: value }; }
      return row;
    });
    formik.setFieldValue('childData', newChildData);
  };

  const actionOptions = [
    { label: 'Approve', value: 'approved' },
    { label: 'Reject', value: 'rejected' },
  ];

  const handleStatusChange = (rowId, value) => {
    const newChildData = values.childData.map((row) => {
      if (row.id === rowId) { return { ...row, status: value }; }
      return row;
    });
    formik.setFieldValue('childData', newChildData);
  }

  const handleAvgRuleChange = (rowId, value) => {
    const newChildData = values.childData.map((row) => {
      if (rowId && row.id === value.id) {
        return { ...row, avgRule_index: rowId.label, avgRule_id: rowId.id }
      } else if (!rowId && row.id === value.id) {
        return { ...row, avgRule_index: '', avgRule_id: '' }
      }
      return row;
    });
    formik.setFieldValue('childData', newChildData);
  }

  const handleButtonClick = (rowData) => {
    if (!rowData.avgRule_id && !rowData.avgRule_index &&
      (!avgRule || (avgRule && !groupId)) && !groupId) {
      const newChildData = values.childData.map((row) => {
        if (row.id === rowData.id) {
          return { ...row, avgSales_index: '', avgSales_error: 'Average Rule must be selected..!' }
        }
        return row;
      });
      formik.setFieldValue('childData', newChildData);
      return false
    }

    let avgRuleIdSelected = ''
    if (rowData.avgRule_id && rowData.avgRule_index) {
      avgRuleIdSelected = rowData.avgRule_id
    } else {
      avgRuleIdSelected = groupId.id
    }
    let customerNo = rowData.customerCode
    setIsSalesDataLoading(true);
    SchemeStatusService.getSapSchemeSalesData(customerNo,
      avgRuleIdSelected, productId, brandId, categoryId, materialId, schemeId).then((res) => {
        if (res.status == 200) {
          if (res.data.SAP_STATUS == 1) {
            let salesValue = res.data.totalTonage ? res.data.totalTonage : 0
            const newChildData = values.childData.map((row) => {
              if (row.id === rowData.id) {
                return { ...row, avgSales_index: salesValue, avgSales_error: '' }
              }
              return row;
            });
            formik.setFieldValue('childData', newChildData);
          } else {
            const newChildData = values.childData.map((row) => {
              if (row.id === rowData.id) {
                return { ...row, avgSales_index: '', avgSales_error: res.data.SAP_REMARKS }
              }
              return row;
            });
            formik.setFieldValue('childData', newChildData);
          }
        } else {
          alert('Something Went Wrong..')
        }
      }).finally(() => {
        setIsSalesDataLoading(false);
      });
  };

  const [isGlobalSalesLoading, setIsGlobalSalesLoading] = useState(false);

  const getAllSalesByRuleId = (rowData) => {
    setIsSalesDataLoading(true);
    setIsGlobalSalesLoading(true);
    const promises = values.childData.map(vk => {
      const customerNo = vk.customerCode;
      return SchemeStatusService.getSapSchemeSalesData(customerNo, rowData.id,
        productId, brandId, categoryId, materialId);
    });

    Promise.all(promises)
      .then(results => {
        const newChildData = values.childData.map((vk, index) => {
          const res = results[index];
          if (res.status === 200) {
            if (res.data.SAP_STATUS == 1) {
              const salesValue = res.data.totalTonage ? res.data.totalTonage : 0;
              return { ...vk, avgSales_index: salesValue, avgSales_error: '' };
            } else {
              return { ...vk, avgSales_index: '', avgSales_error: res.data.SAP_REMARKS };
            }
          } else {
            return { ...vk, avgSales_error: 'Something went wrong' };
          }
        });
        formik.setFieldValue('childData', newChildData);
      })
      .catch(error => {
        console.error("Error fetching sales data:", error);
      })
      .finally(() => {
        setIsSalesDataLoading(false);
        setIsGlobalSalesLoading(false);
      });
  };

  const statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ];

  const [editingRowId, setEditingRowId] = useState(null);

  const tableColumns = commonColumns({
    theme, isParentColumns: true, handleActionChange, actionOptions,
    handleAvgRuleChange, handleStatusChange, statusOptions,
    averageRuleOptions: mappedAverageRuleOptions, formik, handleButtonClick, avgRule, groupId,
    filteredData: filteredChildData,
    editingRowId,       // ✅ ADD THIS
    setEditingRowId,    // ✅ ADD THIS
    productName: values?.productName || values?.schemeName || values?.productId?.label,
  });

  const changeValuesInput = (e) => {
    if (e == 1) { setAvgRule(true) } else { setAvgRule(false) }
    values.groupId = ''
  }


  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      {isSalesDataLoading && <Spinner />}
      {
        getEditSchemesListMasterStatus === apiStatuses.pending && isEditMode
        && <Spinner />
      }
      {getEditSchemesListMasterStatus === apiStatuses.rejected && isEditMode && (
        <ErrorInPage message={getEditSchemesListMasterErrMsg} />
      )}
      {(getEditSchemesListMasterStatus === apiStatuses.succeeded || !isEditMode) &&
        <form onSubmit={(e) => e.preventDefault()}>
          <Grid>
            {/* ... Inputs ... */}
            {/* <Stack direction="row" gap={2}> */}
            <Stack
              direction="row"
              gap={2}
              sx={{
                width: '2050px',
                alignItems: 'center',
                // Prevents the "infinite loop" by ensuring flex-shrink doesn't collapse to 0
                '& > *': { flex: 1, minWidth: 0 }
              }}
            >
              <Grid>
                <TextInput
                  placeholder="scheme-name"
                  size="small"
                  fullWidth
                  value={schemeName}
                  inputLabel="scheme-name"
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid>
                <TextInput
                  placeholder="scheme-id"
                  size="small"
                  fullWidth
                  value={schemeId}
                  inputLabel="scheme-id"
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid>
                <TextInput
                  placeholder="scheme-period"
                  size="small"
                  fullWidth
                  value={`${schemeMonth} - ${schemeYear}`}
                  inputLabel="scheme-period"
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid>
                <TextInput
                  placeholder="product-name"
                  size="small"
                  fullWidth
                  // IMPORTANT: Ensure this value displays the product name
                  value={schemeName}
                  inputLabel="product-name"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid>
                <TextInput
                  placeholder="no-of-customers"
                  size="small"
                  fullWidth
                  value={noOfCustomerCount}
                  formik={formik}
                  inputLabel="no-of-customers"
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Col span={3}>
                <Input
                  value={totalSales}
                  readOnly
                  style={{
                    padding: '15px',
                    fontWeight: "bold",
                    fontSize: "15px",
                    color: "#09069E",
                    width: "200px",
                  }}
                  prefix={'Tot.Sales MTS - '}
                />
              </Col>
              <Col span={3}>
                <Input
                  value={indianNumberFormatComponent(Number(calculatedTotalSchemeAmount) || 0)}
                  readOnly
                  style={{
                    padding: '15px',
                    fontWeight: "bold",
                    fontSize: "15px",
                    color: "#09069E",
                    width: "250px",
                  }}
                  prefix={'Tot.Sch-Amt - '}
                />
              </Col>
              <Col span={3}>
                <Input
                  value={indianNumberFormatComponent(Number(selectedTotalSchemeAmount) || 0)}
                  readOnly
                  style={{
                    padding: '15px',
                    fontWeight: "bold",
                    fontSize: "15px",
                    color: "#1b5e20",
                    width: "250px",
                  }}
                  prefix={'Sel.Sch-Amt - '}
                />
              </Col>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SwitchInput
                  inputLabel="avg-rule-for-all"
                  leftLabel="no"
                  rightLabel="yes"
                  value={avgRule}
                  onChange={(e) => changeValuesInput(e)}
                  name="activeStatus"
                />
              </Grid>
              {avgRule &&
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <Dropdown
                    options={mappedAverageRuleOptions}
                    loading={getDropdownListLoading}
                    placeholder="average-rule"
                    inputLabel="average-rule"
                    value={groupId}
                    formik={formik}
                    name="groupId"
                  />
                </Grid>
              }

              {avgRule && groupId && (
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <Button
                    style={{ marginTop: "5%" }}
                    color="info"
                    variant="contained"
                    // Use local state here
                    disabled={isGlobalSalesLoading}
                    onClick={() => getAllSalesByRuleId(groupId)}
                    startIcon={
                      isGlobalSalesLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                  >
                    {isGlobalSalesLoading ? "Fetching..." : "Get Sales"}
                  </Button>
                </Grid>
              )}
              {targetWorkingFile && (
                <AnimateButton>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => window.open(apiBaseUrl + 'download-target-file?path=' + encodeURIComponent(targetWorkingFile), '_blank')}
                  >
                    Download Target File
                  </Button>
                </AnimateButton>
              )}

              <AnimateButton>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleExport}
                  disabled={!childData || childData.length === 0}
                >
                  Export Table
                </Button>
              </AnimateButton>
            </Stack>

            {/* 4. ADD FILTER BUTTONS HERE (Right above the CustomTable) */}
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
              <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
                <TextInput
                  placeholder="search-customer-name-code"
                  label="search-customer"
                  size="small"
                  sx={{ width: '310px', ...filter3dStyle }}
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  rows={3}
                />
                {/* <TextInput
                  placeholder="search-rs-per-kg"
                  label="search-rs-per-kg"
                  size="small"
                  sx={{ width: '210px', ...filter3dStyle }}
                  value={searchRate}
                  onChange={(e) => setSearchRate(e.target.value)}
                /> */}
              </Stack>

              <ButtonGroup variant="outlined" aria-label="customer eligibility filter">
                <Button
                  variant={filterType === 'ALL' ? 'contained' : 'outlined'}
                  onClick={() => setFilterType('ALL')}
                >
                  All ({baseData.length})
                </Button>
                <Button
                  color="success"
                  variant={filterType === 'Eligible' ? 'contained' : 'outlined'}
                  onClick={() => setFilterType('Eligible')}
                >
                  Eligible ({baseData.filter(row => {
                    if (isMaida) {
                      if (Number(row.eligible) === 1) return true;
                      let targets = row.targetMts || {};
                      if (typeof targets === 'string' && targets.startsWith('{')) {
                        try { targets = JSON.parse(targets); } catch (e) { targets = {}; }
                      }
                      if (!targets || typeof targets !== 'object') targets = {};
                      const sales = row.monthSales || {};
                      return Object.keys(targets).some(cat => {
                        const t = Number(targets[cat] || 0);
                        const s = Number(sales[cat] || 0);
                        return t > 0 && s >= t;
                      });
                    }
                    return Number(row.eligible) === 1;
                  }).length})
                </Button>
                <Button
                  color="error"
                  variant={filterType === 'Not Eligible' ? 'contained' : 'outlined'}
                  onClick={() => setFilterType('Not Eligible')}
                >
                  Not Eligible ({baseData.filter(row => {
                    if (isMaida) {
                      let targets = row.targetMts || {};
                      if (typeof targets === 'string' && targets.startsWith('{')) {
                        try { targets = JSON.parse(targets); } catch (e) { targets = {}; }
                      }
                      if (!targets || typeof targets !== 'object') targets = {};
                      const sales = row.monthSales || {};
                      const hasDefinedTarget = Object.values(targets).some(t => Number(t) > 0);
                      if (!hasDefinedTarget) return false;
                      const metAnyTarget = Object.keys(targets).some(cat => {
                        const t = Number(targets[cat] || 0);
                        const s = Number(sales[cat] || 0);
                        return t > 0 && s >= t;
                      });
                      return !metAnyTarget;
                    }
                    const sales = Number(row.totalSalesVolume || 0);
                    const target = Number(row.targetMts || row.minMts || row.maxMts || 0);
                    return target > 0 && sales < target;
                  }).length})
                </Button>
                <Button
                  color="warning"
                  variant={filterType === 'Unplanned' ? 'contained' : 'outlined'}
                  onClick={() => setFilterType('Unplanned')}
                >
                  Unplanned ({baseData.filter(r => r.id === null).length})
                </Button>
                <Button
                  color="error"
                  variant={filterType === 'Below 5 Tons' ? 'contained' : 'outlined'}
                  onClick={() => setFilterType('Below 5 Tons')}
                >
                  Below 5 Tons ({baseData.filter(r => Number(r.totalSalesVolume || 0) < 5).length})
                </Button>
                <Button
                  color="error"
                  variant={filterType === 'Below 10 Tons' ? 'contained' : 'outlined'}
                  onClick={() => setFilterType('Below 10 Tons')}
                >
                  Below 10 Tons ({baseData.filter(r => Number(r.totalSalesVolume || 0) < 10).length})
                </Button>
                <Button
                  color="error"
                  variant={filterType === 'Zero Amount' ? 'contained' : 'outlined'}
                  onClick={() => setFilterType('Zero Amount')}
                >
                  Zero Amount ({baseData.filter(r => getRowSchemeAmount(r) === 0).length})
                </Button>
              </ButtonGroup>
            </Grid>

            <Grid item xs={12}>
              <CustomTable
                // data={childData && childData.length > 0 ? childData : []} 
                columns={tableColumns}
                data={filteredChildData}
                apiStatus={getEditSchemesListMasterStatus}
                apiErrorMsg={getEditSchemesListMasterErrMsg}
                initialPageSize={10}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" spacing={3} className="btn-end">
                <AnimateButton>
                  <Button variant="contained" color="warning" onClick={goBack}>
                    <FormattedMessage id="back" />
                  </Button>
                </AnimateButton>

                <AnimateButton>
                  <Button
                    variant="contained"
                    color="error"
                    disabled={rejectSchemesListMasterLoading}
                    onClick={(e) => {
                      if (e.detail !== 0) {
                        const selectedCount = values.childData?.filter(row => Number(row.selected) === 1).length || 0;
                        if (selectedCount === 0) {
                          toast.error("Rejection process requires at least one selected customer");
                          return;
                        }
                        Swal.fire({
                          title: 'Are you sure?',
                          text: "You are about to reject the selected customers!",
                          icon: 'warning',
                          showCancelButton: true,
                          confirmButtonColor: '#d33',
                          cancelButtonColor: '#3085d6',
                          confirmButtonText: 'Yes, reject it!'
                        }).then((result) => {
                          if (result.isConfirmed) {
                            rejectProcess(values.childData);
                          }
                        });
                      }
                    }}
                  >
                    {rejectSchemesListMasterLoading ? <CircularProgress size={24} color="inherit" /> : <FormattedMessage id="Reject" />}
                  </Button>
                </AnimateButton>
                <AnimateButton>
                  <Button
                    type="button"
                    variant="contained"
                    disabled={updateSchemesListMasterLoading}
                    onClick={(e) => {
                      // e.detail is 0 for keyboard (Enter/Space), >0 for mouse clicks
                      if (e.detail !== 0) {
                        const selectedCount = values.childData?.filter(row => Number(row.selected) === 1 || row.selected === true).length || 0;
                        if (selectedCount === 0) {
                          toast.error("No customers selected!");
                          return;
                        }
                        Swal.fire({
                          title: 'Are you sure?',
                          text: "You are about to save the scheme status!",
                          icon: 'warning',
                          showCancelButton: true,
                          confirmButtonColor: '#0c8e2aff',
                          cancelButtonColor: '#d33',
                          confirmButtonText: 'Yes, save it!'
                        }).then((result) => {
                          if (result.isConfirmed) {
                            formik.handleSubmit();
                          }
                        });
                      }
                    }}
                  >
                    {updateSchemesListMasterLoading ? <CircularProgress size={24} color="inherit" /> : <FormattedMessage id="save" />}
                  </Button>
                </AnimateButton>
              </Stack>
            </Grid>
          </Grid>
        </form>
      }
    </>
  );
}

CreateEditSchemeStatusMaster.propTypes = {
  isEditMode: PropTypes.bool,
};

CreateEditSchemeStatusMaster.defaultProps = {
  isEditMode: false,
};

export default CreateEditSchemeStatusMaster;